import React, { useState } from 'react';
import { X, Phone, Mail, MapPin, Calendar, Check, Trash2, MessageSquare, ArrowRight, Edit3 } from 'lucide-react';
import {
  useGetLeadQuery, useLeadActivitiesQuery, useAddLeadActivityMutation,
  useUpdateLeadStatusMutation, useClassifyLeadMutation, useConvertLeadMutation,
  useAddLeadFollowUpMutation,
} from '../../api/leadsApi';
import { LEAD_STATUS, LEAD_PRIORITY } from '../../utils/status';
import EditLeadModal from './EditLeadModal';
import FollowUpDateModal from './FollowUpDateModal';
import ConfirmDialog from '../../components/ConfirmDialog';
import ScheduleConsultationModal from '../consultations/ScheduleConsultationModal';
import { toast } from 'react-toastify';

export default function LeadDetailDrawer({ leadId, onClose }) {
  const { data: lead, isLoading } = useGetLeadQuery(leadId, { skip: !leadId });
  const { data: activities = [] } = useLeadActivitiesQuery(leadId, { skip: !leadId });
  const [addActivity] = useAddLeadActivityMutation();
  const [updateStatus] = useUpdateLeadStatusMutation();
  const [classify] = useClassifyLeadMutation();
  const [convert] = useConvertLeadMutation();
  const [addFollowUp] = useAddLeadFollowUpMutation();
  const [showFollowUpDate, setShowFollowUpDate] = useState(false);
  const [customContactDate, setCustomContactDate] = useState('');
  const [note, setNote] = useState('');
  const [activityType, setActivityType] = useState('Note');
  const [showEdit, setShowEdit] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [confirmConvert, setConfirmConvert] = useState(false);

  if (!leadId) return null;

  const status = lead && (LEAD_STATUS[lead.status] || { label: lead.status, color: 'b-gray' });
  const pri = lead && (LEAD_PRIORITY[lead.priority] || { label: lead.priority, color: 'b-gray' });

  const handleAddActivity = async () => {
    if (!note.trim()) return;
    try {
      await addActivity({ id: leadId, activityType, body: note }).unwrap();
      setNote('');
      toast.success('Activity logged');
    } catch { toast.error('Failed to log activity'); }
  };

  const handleStatus = async (s) => {
    try { await updateStatus({ id: leadId, status: s }).unwrap(); toast.success(`Status → ${s}`); }
    catch { toast.error('Failed to update status'); }
  };

  const handleMarkJunk = async () => {
    const reason = window.prompt('Junk reason?');
    if (reason === null) return;
    try { await classify({ id: leadId, classification: 'Junk', reason }).unwrap(); toast.success('Marked as junk'); onClose(); }
    catch { toast.error('Failed to classify'); }
  };

  // "Contact after X" — records a callback promise; the Today's Tasks engine surfaces the
  // lead automatically on the target date.
  const scheduleContact = async (days, explicitDate) => {
    const when = explicitDate
      ? new Date(`${explicitDate}T09:00:00+05:30`) // 9 AM IST on the chosen day
      : new Date(Date.now() + days * 86400000);
    try {
      await addFollowUp({ id: leadId, scheduledAt: when.toISOString(), reason: 'Callback promised' }).unwrap();
      setCustomContactDate('');
      toast.success(`Callback scheduled for ${when.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}`);
    } catch (e) { toast.error(e?.data?.error?.message || 'Failed to schedule callback'); }
  };

  const performConvert = async () => {
    setConfirmConvert(false);
    try {
      const patient = await convert({ id: leadId, gender: 'Unspecified' }).unwrap();
      toast.success(`Patient ${patient.patientCode} created`);
      onClose();
    } catch (e) { toast.error(e?.data?.error?.message || 'Failed to convert'); }
  };

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isLoading ? 'Loading…' : (lead?.fullName ?? 'Lead')}</h2>
          <div style={{ display: 'flex', gap: 6 }}>
            {lead && <button className="btn btn-sm btn-outline" onClick={() => setShowEdit(true)}><Edit3 size={14} /> Edit</button>}
            <button className="modal-close" onClick={onClose}><X size={18} /></button>
          </div>
        </div>
        <div className="modal-body">
          {lead && (
            <>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                <span className={`badge ${status.color}`}>{status.label}</span>
                <span className={`badge ${pri.color}`}>{pri.label}</span>
                <span className="badge b-gray">{lead.source}</span>
              </div>
              <div className="card card-body" style={{ marginBottom: 16 }}>
                <div className="info-row"><div className="ir-label">Phone</div><div className="ir-value"><Phone size={14} /> {lead.phoneNumber}</div></div>
                {lead.email && <div className="info-row"><div className="ir-label">Email</div><div className="ir-value"><Mail size={14} /> {lead.email}</div></div>}
                {lead.city && <div className="info-row"><div className="ir-label">City</div><div className="ir-value"><MapPin size={14} /> {lead.city}</div></div>}
                <div className="info-row"><div className="ir-label">Created</div><div className="ir-value"><Calendar size={14} /> {new Date(lead.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</div></div>
                {lead.notes && <div className="info-row"><div className="ir-label">Notes</div><div className="ir-value">{lead.notes}</div></div>}
              </div>

              {(lead.utmSource || lead.utmMedium || lead.utmCampaign || lead.adSetName || lead.adName) && (
                <div className="card card-body" style={{ marginBottom: 16 }}>
                  <h3 style={{ fontSize: '0.85rem', marginBottom: 8, color: 'var(--text-secondary)' }}>Ad campaign attribution</h3>
                  {lead.utmSource && <div className="info-row"><div className="ir-label">Source</div><div className="ir-value">{lead.utmSource}</div></div>}
                  {lead.utmMedium && <div className="info-row"><div className="ir-label">Medium / Placement</div><div className="ir-value">{lead.utmMedium}</div></div>}
                  {lead.utmCampaign && <div className="info-row"><div className="ir-label">Campaign</div><div className="ir-value">{lead.utmCampaign}</div></div>}
                  {lead.adSetName && <div className="info-row"><div className="ir-label">Ad set</div><div className="ir-value">{lead.adSetName}</div></div>}
                  {lead.adName && <div className="info-row"><div className="ir-label">Ad</div><div className="ir-value">{lead.adName}</div></div>}
                </div>
              )}

              <h3 style={{ fontSize: '0.9rem', marginBottom: 8, color: 'var(--text-secondary)' }}>Quick actions</h3>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
                <button className="btn btn-sm btn-outline" onClick={() => handleStatus('NotConnected')}>Not Connected</button>
                {lead.status !== 'FollowUpPending' && (
                  <button className="btn btn-sm btn-outline" onClick={() => setShowFollowUpDate(true)}>Follow-up</button>
                )}
                <button className="btn btn-sm btn-outline" onClick={() => setShowSchedule(true)}>Move to Consultation</button>
                <button className="btn btn-sm btn-success" onClick={() => setConfirmConvert(true)}><ArrowRight size={14} /> Convert</button>
                <button className="btn btn-sm btn-danger" onClick={handleMarkJunk}><Trash2 size={14} /> Junk</button>
              </div>

              <h3 style={{ fontSize: '0.9rem', marginBottom: 8, color: 'var(--text-secondary)' }}>Contact after</h3>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
                <button className="btn btn-sm btn-outline" onClick={() => scheduleContact(3)}>3 days</button>
                <button className="btn btn-sm btn-outline" onClick={() => scheduleContact(7)}>1 week</button>
                <button className="btn btn-sm btn-outline" onClick={() => scheduleContact(14)}>2 weeks</button>
                <input
                  className="form-control" type="date" style={{ width: 150 }}
                  value={customContactDate} onChange={(e) => setCustomContactDate(e.target.value)}
                  title="Pick an exact callback date"
                />
                <button className="btn btn-sm btn-primary" disabled={!customContactDate}
                  onClick={() => scheduleContact(0, customContactDate)}>
                  Set
                </button>
                <span className="td-muted" style={{ fontSize: '0.74rem' }}>
                  Surfaces in Today's Tasks on the target date.
                </span>
              </div>

              <h3 style={{ fontSize: '0.9rem', marginBottom: 8, color: 'var(--text-secondary)' }}>Log activity</h3>
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                <select className="form-control" style={{ maxWidth: 140 }} value={activityType} onChange={(e) => setActivityType(e.target.value)}>
                  <option value="Note">Note</option>
                  <option value="Call">Call</option>
                  <option value="WhatsApp">WhatsApp</option>
                  <option value="Sms">SMS</option>
                  <option value="Email">Email</option>
                </select>
                <input className="form-control" placeholder="What happened?" value={note} onChange={(e) => setNote(e.target.value)} />
                <button className="btn btn-primary" onClick={handleAddActivity}><Check size={14} /> Log</button>
              </div>

              <h3 style={{ fontSize: '0.9rem', margin: '16px 0 8px', color: 'var(--text-secondary)' }}>Activity timeline</h3>
              <div className="timeline">
                {activities.map((a) => (
                  <div key={a.id} className="tl-item">
                    <div className="tl-dot" style={{ background: 'var(--blue-50)', color: 'var(--blue-700)' }}>
                      <MessageSquare size={14} />
                    </div>
                    <div className="tl-line" />
                    <div className="tl-content">
                      <div className="tl-text"><strong>{a.subject || a.activityType}</strong>{a.body ? ` — ${a.body}` : ''}</div>
                      <div className="tl-time">{new Date(a.occurredAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</div>
                    </div>
                  </div>
                ))}
                {activities.length === 0 && <div className="empty-state"><p>No activity yet.</p></div>}
              </div>
            </>
          )}
        </div>
      </div>

      {showFollowUpDate && (
        <FollowUpDateModal
          leadName={lead?.fullName}
          onClose={() => setShowFollowUpDate(false)}
          onConfirm={async (whenIso) => {
            try {
              await updateStatus({ id: leadId, status: 'FollowUpPending' }).unwrap();
              await addFollowUp({ id: leadId, scheduledAt: whenIso, reason: 'Follow-up scheduled' }).unwrap();
              toast.success(`Follow-up on ${new Date(whenIso).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })} — will appear in Today's Tasks`);
            } catch (e) {
              toast.error(e?.data?.error?.message || 'Failed to schedule the follow-up');
            } finally {
              setShowFollowUpDate(false);
            }
          }}
        />
      )}
      {showEdit && <EditLeadModal lead={lead} onClose={() => setShowEdit(false)} />}
      {showSchedule && (
        <ScheduleConsultationModal
          leadId={leadId}
          subjectName={lead?.fullName}
          onClose={() => setShowSchedule(false)}
          onCreated={onClose}
        />
      )}
      <ConfirmDialog
        open={confirmConvert}
        title="Convert lead to patient?"
        message={`A new patient record will be created from ${lead?.fullName ?? 'this lead'}. This cannot be undone.`}
        confirmLabel="Convert"
        variant="success"
        onConfirm={performConvert}
        onCancel={() => setConfirmConvert(false)}
      />
    </div>
  );
}
